import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Glasses, ShoppingCart } from 'lucide-react';
import {
  RecommendationMatchLevel,
  SupportedLocale,
  type FrameRecommendationItem,
  type RecommendationStockScope,
} from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useLocale } from '@/hooks/useLocale';
import { formatMoney } from '@/modules/catalog/money';
import { noteText, reasonText } from '../explanations';
import { StockAvailability } from './StockAvailability';

const MATCH_LEVEL_TONES: Record<RecommendationMatchLevel, 'success' | 'brand' | 'neutral'> = {
  [RecommendationMatchLevel.BEST_MATCH]: 'success',
  [RecommendationMatchLevel.GOOD_MATCH]: 'brand',
  [RecommendationMatchLevel.OTHER_OPTIONS]: 'neutral',
  [RecommendationMatchLevel.UNRANKED]: 'neutral',
};

/**
 * One recommended frame, as a counter would present it.
 *
 * Retail facts first, then the match level and the reasons behind it, then quieter notes about what the catalogue does not record. There is no score, percentage or confidence to show, because the server sends none. Stock appears only when the server sent it; without it the card says nothing about stock at all.
 *
 * Add to sale appears only when the screen passes a handler for it: for someone who may both recommend frames and sell, and only for a frame the server signed a selection for. Start optical order is its own action beside it, for someone who may also take an optical order: it opens the builder with this frame, and changes nothing about Add to sale.
 */
export function RecommendationCard({
  item,
  stockScope,
  canViewFrame,
  onAddToSale,
  onStartOpticalOrder,
}: {
  item: FrameRecommendationItem;
  /** Where the server looked for stock; null when the user may not see stock. */
  stockScope: RecommendationStockScope | null;
  canViewFrame: boolean;
  onAddToSale?: () => void;
  onStartOpticalOrder?: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const name =
    locale === SupportedLocale.AR && item.productNameAr ? item.productNameAr : item.productName;
  // The same frame in two colours sits side by side, so the sale action names the variant as well.
  const variantLabel = item.variantName ? `${name} — ${item.variantName}` : name;
  const reasons = item.reasons.flatMap((reason) => {
    const explanation = reasonText(t, reason, item);
    return explanation ? [explanation] : [];
  });
  const notes = item.notes.flatMap((note) => {
    const text = noteText(t, note);
    return text ? [text] : [];
  });
  // A missing dimension stays a quiet dash, so a frame with sizes unrecorded does not shout about it.
  const size = (value: number | null) =>
    value === null ? <span className="text-muted/70">—</span> : <span>{value}</span>;
  const spokenSize = (value: number | null) =>
    value === null
      ? t('frameRecommendations.card.notRecorded')
      : t('frameRecommendations.card.millimetres', { value });
  const retailFacts = [
    item.brand?.name,
    item.colourName,
    item.frameShape ? t(`catalog.frameShape.${item.frameShape}`) : null,
  ].filter((fact): fact is string => Boolean(fact));

  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start gap-3 p-4">
        {/* The catalogue keeps no frame images yet, so every card shows the same quiet placeholder. */}
        <span
          aria-hidden
          className="bg-canvas text-muted grid size-14 shrink-0 place-items-center rounded-lg"
        >
          <Glasses className="size-7" />
        </span>

        <div className="min-w-0 flex-1 space-y-1">
          <Badge tone={MATCH_LEVEL_TONES[item.matchLevel]}>
            {t(`frameRecommendations.matchLevel.${item.matchLevel}`)}
          </Badge>
          <h3 className="text-ink text-sm leading-snug font-semibold break-words">{name}</h3>
          {item.variantName ? (
            <p className="text-muted text-xs break-words">{item.variantName}</p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 px-4 pb-4">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
          {retailFacts.length > 0 ? (
            <>
              <dt className="sr-only">{t('frameRecommendations.card.details')}</dt>
              <dd className="text-ink col-span-2 text-sm">
                {retailFacts.map((fact, index) => (
                  <span key={fact}>
                    {index > 0 ? ' · ' : null}
                    {/* Each fact is isolated, so a Latin brand beside an Arabic shape keeps its place in either direction. */}
                    <bdi>{fact}</bdi>
                  </span>
                ))}
              </dd>
            </>
          ) : null}
          <dt className="text-muted">{t('frameRecommendations.card.sku')}</dt>
          <dd className="text-ink font-mono break-all">
            <bdi dir="ltr">{item.sku}</bdi>
          </dd>
          <dt className="text-muted">{t('frameRecommendations.card.size')}</dt>
          <dd className="text-ink">
            <span dir="ltr" aria-hidden>
              {size(item.lensWidthMm)} □ {size(item.bridgeWidthMm)} – {size(item.templeLengthMm)}
            </span>
            <span className="sr-only">
              {t('frameRecommendations.card.sizeLabel', {
                lens: spokenSize(item.lensWidthMm),
                bridge: spokenSize(item.bridgeWidthMm),
                temple: spokenSize(item.templeLengthMm),
              })}
            </span>
          </dd>
        </dl>

        <p className="text-ink text-base font-semibold" dir="auto">
          {formatMoney(item.priceMinor, locale, item.currencyCode)}
        </p>

        {reasons.length > 0 ? (
          <ul className="space-y-1" aria-label={t('frameRecommendations.card.reasons')}>
            {reasons.map((reason, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" aria-hidden />
                <span className="min-w-0">
                  <span className="text-ink">{reason.text}</span>
                  {reason.detail ? <span className="text-muted"> · {reason.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {notes.length > 0 ? (
          <ul
            className="text-muted space-y-0.5 text-xs"
            aria-label={t('frameRecommendations.card.notes')}
          >
            {notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        ) : null}

        <div className="mt-auto space-y-3">
          {item.stock && stockScope ? (
            <StockAvailability stock={item.stock} scope={stockScope} />
          ) : null}

          {onAddToSale || onStartOpticalOrder || canViewFrame ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              {onAddToSale ? (
                <Button
                  size="sm"
                  onClick={onAddToSale}
                  aria-label={t('frameRecommendations.card.addToSaleNamed', { name: variantLabel })}
                >
                  <ShoppingCart className="size-4" aria-hidden />
                  {t('frameRecommendations.card.addToSale')}
                </Button>
              ) : null}
              {onStartOpticalOrder ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onStartOpticalOrder}
                  aria-label={t('frameRecommendations.card.startOpticalOrderNamed', {
                    name: variantLabel,
                  })}
                >
                  <Glasses className="size-4" aria-hidden />
                  {t('frameRecommendations.card.startOpticalOrder')}
                </Button>
              ) : null}
              {canViewFrame ? (
                <Link
                  to={`/inventory/products/${item.productId}`}
                  className="text-brand-700 hover:text-brand-800 inline-flex min-h-9 items-center text-sm font-medium"
                  aria-label={t('frameRecommendations.card.viewNamed', { name })}
                >
                  {t('frameRecommendations.card.view')}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
